import { syncRequest } from "./api";
import {
  getSince,
  setSince,
  getDirtyIds,
  clearDirty,
  saveIdea,
  saveMessage,
  saveProject,
  saveRepo,
  db,
} from "./db";
import {
  ideaToWire,
  ideaFromWire,
  messageToWire,
  messageFromWire,
  projectToWire,
  projectFromWire,
  repoToWire,
  repoFromWire,
} from "./types";
import type { SyncStatus } from "./types";

type SyncStatusCallback = (status: SyncStatus) => void;

let syncInProgress = false;
let statusCallback: SyncStatusCallback | null = null;
let debounceTimer: ReturnType<typeof setTimeout> | null = null;

export function setSyncStatusCallback(cb: SyncStatusCallback): void {
  statusCallback = cb;
}

function setStatus(status: SyncStatus): void {
  statusCallback?.(status);
}

export async function sync(_userId: string): Promise<void> {
  if (!navigator.onLine) {
    setStatus({ kind: "offline" });
    return;
  }
  if (syncInProgress) return;
  syncInProgress = true;
  setStatus({ kind: "syncing" });

  try {
    const since = await getSince();
    const dirty = await getDirtyIds();

    // Collect dirty rows
    const dirtyIdeas = await Promise.all(
      dirty.ideas.map((id) => db.ideas.get(id)),
    );
    const dirtyMessages = await Promise.all(
      dirty.messages.map((id) => db.messages.get(id)),
    );
    const dirtyProjects = await Promise.all(
      dirty.projects.map((id) => db.projects.get(id)),
    );

    // Repos use composite key "userId:slug"
    const dirtyRepos = await Promise.all(
      dirty.repos.map((key) => {
        const [uid, ...slugParts] = key.split(":");
        const slug = slugParts.join(":");
        return db.repos.get([uid, slug]);
      }),
    );

    const payload = {
      since,
      changes: {
        ideas: dirtyIdeas.filter(Boolean).map((i) => ideaToWire(i!)),
        messages: dirtyMessages.filter(Boolean).map((m) => messageToWire(m!)),
        projects: dirtyProjects.filter(Boolean).map((p) => projectToWire(p!)),
        repos: dirtyRepos.filter(Boolean).map((r) => repoToWire(r!)),
      },
    };

    const res = await syncRequest(payload);
    if (!res.ok) {
      throw new Error(`Sync failed: ${res.status}`);
    }

    const { serverTime, changes } = (await res.json()) as {
      serverTime: number;
      changes: {
        ideas: ReturnType<typeof ideaToWire>[];
        messages: ReturnType<typeof messageToWire>[];
        projects: ReturnType<typeof projectToWire>[];
        repos: ReturnType<typeof repoToWire>[];
      };
    };

    // Apply server changes (LWW: save only if server is newer)
    await Promise.all([
      ...changes.ideas.map((w) => saveIdea(ideaFromWire(w))),
      ...changes.messages.map((w) => saveMessage(messageFromWire(w))),
      ...changes.projects.map((w) => saveProject(projectFromWire(w))),
      ...changes.repos.map((w) => saveRepo(repoFromWire(w))),
    ]);

    await setSince(serverTime);
    await clearDirty();

    setStatus({ kind: "synced", at: serverTime });
  } catch (err: unknown) {
    const msg = err instanceof Error ? err.message : "Sync error";
    console.error("Sync error:", err);
    if (!navigator.onLine) {
      setStatus({ kind: "offline" });
    } else {
      setStatus({ kind: "error", message: msg });
    }
  } finally {
    syncInProgress = false;
  }
}

export function scheduleSyncDebounced(userId: string, delayMs = 1000): void {
  if (debounceTimer) clearTimeout(debounceTimer);
  debounceTimer = setTimeout(() => sync(userId), delayMs);
}

export function initSyncListeners(userId: string): () => void {
  const onOnline = () => sync(userId);
  const onVisibility = () => {
    if (document.visibilityState === "visible") sync(userId);
  };

  window.addEventListener("online", onOnline);
  window.addEventListener("offline", () => setStatus({ kind: "offline" }));
  document.addEventListener("visibilitychange", onVisibility);

  // Initial sync
  sync(userId);

  return () => {
    window.removeEventListener("online", onOnline);
    document.removeEventListener("visibilitychange", onVisibility);
  };
}
